import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card"
import {
  TypographyError,
  TypographyH1,
  TypographyP,
} from "@/components/ui/typography"
import {
  InputOTP,
  InputOTPSlot,
  InputOTPGroup,
  InputOTPSeparator,
} from "@/components/ui/input-otp"
import { useAutoTranslation, useUserStore } from "@/entities"
import { LANG_KEYS, QUERY_KEYS } from "@/shared/constants"
import { REGEXP_ONLY_DIGITS } from "input-otp"
import { useEffect, useState, useRef } from "react"
import { Button } from "@/components/ui/button"
import moment from "moment"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  authControllerEmailConfirm,
  authControllerResendEmailCode,
  type EmailConfirmDtoReq,
} from "@/shared/api/generated"
import { AxiosError } from "axios"
import { SvgLogo } from "@/shared/ui/svg"
import { toast } from "sonner"
import styles from "./styles.module.scss"

const formatTime = (totalSeconds: number) => {
  const duration = moment.duration(totalSeconds, "seconds")
  const minutes = Math.floor(duration.asMinutes())
  const remainingSeconds = duration.seconds()

  if (minutes > 0) {
    return `${minutes}m${remainingSeconds.toString().padStart(2, "0")}s`
  } else {
    return `${remainingSeconds}s`
  }
}

const ResendConfirmationCodeWithTimer = ({
  reloadInSeconds,
  onResend,
}: {
  reloadInSeconds?: number
  onResend: () => void
}) => {
  const { t } = useAutoTranslation()
  const initialSeconds = reloadInSeconds || 60
  const [seconds, setSeconds] = useState(initialSeconds)
  const [isTimeout, setTimeout] = useState(false)

  // Function to restart the timer
  const restartTimer = () => {
    setSeconds(initialSeconds)
    setTimeout(false)
  }

  // Handle resend with timer restart
  const handleResend = () => {
    onResend()
    restartTimer()
  }

  useEffect(() => {
    // Don't start interval if timer is already at 0
    if (seconds <= 0) {
      setTimeout(true)
      return
    }

    const interval = setInterval(() => {
      setSeconds(prevSeconds => {
        const newSeconds = prevSeconds - 1
        if (newSeconds <= 0) {
          // Clear the interval immediately when timer reaches 0
          clearInterval(interval)
          setTimeout(true)
          return 0
        }
        return newSeconds
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [seconds])

  // Format time using moment.js in "1m12s" format

  return (
    <div>
      {!isTimeout ? (
        <div className={styles.timeoutWrapper}>
          <TypographyP>{t(LANG_KEYS.RESEND_CONFIRMATION_CODE_IN)}</TypographyP>
          <TypographyP className="!mt-0">({formatTime(seconds)})</TypographyP>
        </div>
      ) : (
        <Button variant="outline" onClick={handleResend} className="mx-auto">
          {t(LANG_KEYS.RESEND_CONFIRMATION_CODE)}
        </Button>
      )}
    </div>
  )
}

export const ConfirmEmailInputCode = () => {
  const { t } = useAutoTranslation()
  const queryClient = useQueryClient()
  const email = useUserStore(s => s.email)
  const [error, setError] = useState()
  const [value, setValue] = useState("")
  const otpInputRef = useRef<HTMLInputElement>(null)

  const confirmMutation = useMutation({
    mutationFn: (data: EmailConfirmDtoReq) => authControllerEmailConfirm(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.HAS_CODE })
    },
    onError: error => {
      if (error instanceof AxiosError) {
        setError(error.response?.data.message ?? "")
      }
    },
  })

  const resendEmailMutation = useMutation({
    mutationFn: authControllerResendEmailCode,

    onSuccess: () => {
      toast.success(
        t(LANG_KEYS.A_CONFIRMATION_CODE_HAS_BEEN_SENT_TO_YOUR_EMAIL)
      )
    },
    onError: error => {
      console.error(error)
    },
  })

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    confirmMutation.mutate({ code: value })
  }

  // Focus OTP input when tab or window is focused
  useEffect(() => {
    const handleFocus = () => {
      if (otpInputRef.current) {
        otpInputRef.current.focus()
      }
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleFocus()
      }
    }

    // Add event listeners
    window.addEventListener("focus", handleFocus)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    // Focus on initial mount
    handleFocus()

    // Cleanup event listeners
    return () => {
      window.removeEventListener("focus", handleFocus)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [])

  return (
    <div className={styles.wrapper}>
      <SvgLogo className="h-16 w-16" variant="enhanced" />

      <form className={styles.form} onSubmit={handleSubmit}>
        <Card>
          <CardHeader className="text-center">
            <TypographyH1>
              {t(LANG_KEYS.ENTER_THE_CONFIRMATION_CODE)}
            </TypographyH1>
            <TypographyP>
              {t(LANG_KEYS.A_CONFIRMATION_CODE_HAS_BEEN_SENT_TO_YOUR_EMAIL)}
            </TypographyP>
            {email && <TypographyP>{email}</TypographyP>}
          </CardHeader>
          <CardContent>
            <div className={styles.otpWrapper}>
              <InputOTP
                ref={otpInputRef}
                maxLength={6}
                pattern={REGEXP_ONLY_DIGITS}
                value={value}
                onChange={value => setValue(value)}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} aria-invalid={!!error} />
                  <InputOTPSlot index={1} aria-invalid={!!error} />
                  <InputOTPSlot index={2} aria-invalid={!!error} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={3} aria-invalid={!!error} />
                  <InputOTPSlot index={4} aria-invalid={!!error} />
                  <InputOTPSlot index={5} aria-invalid={!!error} />
                </InputOTPGroup>
              </InputOTP>
              <div className={styles.otpError}>
                {error && <TypographyError>{error}</TypographyError>}
              </div>
            </div>
          </CardContent>
          <CardFooter className={styles.footer}>
            <Button
              className={styles.confirmButton}
              disabled={value.length !== 6}
              type="submit"
              isLoading={confirmMutation.isPending}
            >
              {t(LANG_KEYS.CONFIRM)}
            </Button>
            <ResendConfirmationCodeWithTimer
              reloadInSeconds={60}
              onResend={() => resendEmailMutation.mutate({})}
            />
          </CardFooter>
        </Card>
      </form>
    </div>
  )
}
